import * as d3 from 'd3-selection'
import defs from './defs.js'
import triangle from './triangle'

function currentLabels (thresholds, dimensions) {
  var height = 0
    , container = thresholds.selectAll('g.current-label')
    , currentValueFontSize = Math.floor((dimensions.gaugeHeight / 10) + 12)

  // Current value label
  container.selectAll('text.value')
           .attr('font-size', currentValueFontSize)

  // Text label
  container.selectAll('text.units')
           .attr('font-size', currentValueFontSize / 2) // units are half the size of the value text font size

  if (container.size()) {
    height = container.node().getBBox().height
  }
  return height
}

function thresholdLabels (thresholds, dimensions, labelMarginPercent) {
   var height = 0
    , minFontSize = 6
    , labels = thresholds.selectAll('g.threshold-label text')
    , defaultFontSize = (dimensions.gaugeHeight / 10) + 5

  function setX (d, i, all) {
    var x = d.position
      , offset = this.getBBox().width / 2 - dimensions.innerPadding
      , labelMarginLeft = dimensions.margin.left * labelMarginPercent
      , labelMarginRight = dimensions.margin.right * labelMarginPercent

    if (i === 0 && offset > labelMarginLeft) {
      x += offset - labelMarginLeft
    }
    if (i === all.length - 1 && offset > labelMarginRight) {
      x -= offset - labelMarginRight
    }

    d._x = x
    return this
  }

  function labelsOverlapping (labels) {
    var minThresholdDistance = 5 // the minimum amount of space between thresholds before they start shrinking

    return !!labels.filter(function (d) {
                     if (!this.nextSibling) {
                       return false
                     }

                     // Check if we're overlapping w/ the next element
                     var endX = d._x + this.getBBox().width / 2
                       , next = d3.select(this.nextSibling)
                       , nextStartX = next.datum()._x - (this.nextSibling.getBBox().width / 2)
                       , overlap = (endX - nextStartX) + minThresholdDistance

                     return overlap > 0
                   })
                  .size()
  }

  labels.attr('font-size', defaultFontSize)
        .select(setX)

  // Now check if the labels are overlapping, and keep shrinking the font size until they are no longer overlapping
  // This isn't elegant, but Nathan couldn't figure out a better solution and defaulted to the shitty code solution.
  while (labelsOverlapping(labels) && labels.attr('font-size') > 0) {
    labels.attr('font-size', function (d) {
            var current = d3.select(this).attr('font-size')
              , adjusted = current - current * .03
              , fontSize = adjusted > minFontSize ? adjusted : 0

            // Store the shrinkage percentage on the labels data object, so we can use it to adjust the labels top margin a bit later.
            d._fontAdjustment = fontSize / defaultFontSize

            return fontSize
          })
          .select(setX)
  }

  if (labels.size()) {
    height = labels.node().getBBox().height
  }

  return height
}

export default function (selection, id, dimensions, scaler, validThresholds) {
  selection.each(function (data) {
    var gauge = d3.select(this)
      , labelMarginPercent = 0.65 // We allow labels to extend 65% of the way into the margin space
      , thresholds = gauge.selectAll('g.thresholds')
                          .attr('transform', 'translate(' + dimensions.innerPadding + ',' + dimensions.innerPadding + ')')
      , currentLabelHeight = currentLabels(thresholds, dimensions)
      , thresholdLabelHeight = thresholdLabels(thresholds, dimensions, labelMarginPercent)
      , gaugeBorderHeight = Math.max(10, dimensions.gaugeHeight - currentLabelHeight - thresholdLabelHeight)
      , coloredBarHeight = Math.max(gaugeBorderHeight - (dimensions.innerPadding * 2), 0)
      , unitsOffset = 3
      , triangleSize = Math.min(coloredBarHeight + 3, ((coloredBarHeight / 3.3) + 15))
      , triangleStrokeWidth = triangleSize * 0.08
      , currentTriangleOffset = dimensions.innerPadding - 1
      , currentTrianglePath = triangle(triangleSize)
      , previousTrianglePath = triangle(triangleSize * 0.8) // previous triangle is smaller the the current one

    d3.select(this.parentNode)
      .call(defs(id, dimensions.cornerRadius, dimensions.gaugeWidth, gaugeBorderHeight, 0, currentLabelHeight))

    // Draw the threshold colors bars
    thresholds.selectAll('rect')
              .attr('y', currentLabelHeight)
              .attr('x', function (d) {
                return d.start
              })
              .attr('height', coloredBarHeight)
              .attr('width', function (d) {
                return d.length
              })
              .style('stroke-width', dimensions.rectStrokeWidth)

    // Position the current triangle
    thresholds.selectAll('path.value-indicator.current')
              .attr('d', currentTrianglePath)
              .attr('transform', function (d) {
                var x = scaler(d.raw) + data.value.offset
                  , y = currentLabelHeight + this.getBBox().height / 2 + dimensions.rectStrokeWidth / 2 // top of triangle lines up with outer rect line
                          - currentTriangleOffset // extend it into the inner padding

                return 'translate(' + x + ',' + y + ')'
              })
              .style('stroke-width', triangleStrokeWidth)

    // Position previous value triangle
    thresholds.selectAll('path.value-indicator.previous')
              .attr('d', previousTrianglePath)
              .attr('transform', function (d) {
                var x = Math.min(dimensions.gaugeWidth - dimensions.innerPadding * 2, scaler(d.value) + d.offset)
                  , y = currentLabelHeight + coloredBarHeight + dimensions.rectStrokeWidth / 2 - this.getBBox().height * 0.5
                return 'translate(' + x + ',' + y + ') rotate(180)'
              })

    // Position the current text labels
    var currentLabelGroup = thresholds.selectAll('g.current-label')
                                      .attr('transform', function (d) {
                                        let x = dimensions.gaugeWidth / 2 // By default, center
                                          , units = this.querySelector('text.units')
                                          , unitsWidth = units ? units.getBBox().width : 0
                                          , textWidth = this.firstChild.getBBox().width + unitsWidth + unitsOffset

                                        if (typeof d.raw !== 'undefined' && validThresholds) {
                                          x = Math.min(dimensions.gaugeWidth - dimensions.innerPadding, scaler(d.raw) + d.offset)
                                        }

                                        let borderRight = dimensions.gaugeWidth + dimensions.margin.right * labelMarginPercent
                                          , borderLeft = -dimensions.margin.left * labelMarginPercent
                                          , textRightEdge = x + (textWidth / 2) + dimensions.innerPadding
                                          , textLeftEdge = x - (textWidth / 2) + dimensions.innerPadding

                                        if (textRightEdge > borderRight) {
                                          // If the text is going to go off the right side of the gauge, move it left
                                          x -= (textRightEdge - borderRight)
                                        } else if (textLeftEdge < borderLeft) {
                                          // If the text is going to go off the left side of the gauge, move it right
                                          x += (borderLeft - textLeftEdge)
                                        }

                                        x = Math.max(0, x) // but don't prevent us from cutting off text on the left
                                        return 'translate(' + x + ',' + (currentLabelHeight - (dimensions.innerPadding * 2)) +  ')'
                                      })

    currentLabelGroup.select('g.text-value')
                     .attr('transform', 'translate(0,0)') // reset in case it flipped from vertical
    currentLabelGroup.select('g.text-units')
                     .attr('transform', function () {
                       var x = this.previousSibling.getBBox().width / 2 // value uses middle text-anchor, so we only have to translate half its width
                       return 'translate(' + (x + unitsOffset) + ',0)'
                     })

    thresholds.selectAll('g.threshold-label')
              .attr('transform', function (d) {
                var topMargin = (thresholdLabelHeight * -0.53) + 4.5
                  , border = gauge.select('.gauge-border').node().getBBox()
                  , actualFontSize = parseFloat(d3.select(this).select('text').attr('font-size'))
                  , y = border.y + border.height + actualFontSize + topMargin

                // Move the threshold labels up if they're being reduced in size due to overlap
                if (d._fontAdjustment > 0) {
                  y -= thresholdLabelHeight * (1 - d._fontAdjustment)
                }
                return 'translate(' + d._x + ',' + y + ')'
              })
  })
}
